<template>
  <div
    :class="{ 'form-group is-invalid': state === false }"
    @mousedown.capture="onOptionMousedown"
  >
    <v-select
      :value="selectedOption"
      :input-id="$attrs.id ? `${$attrs.id}-input` : undefined"
      :options="options"
      :get-option-label="formatOption"
      :filter-by="filterOption"
      :disabled="disabled || readonly"
      :loading="loading"
      :clearable="clearable"
      :state="state"
      :class="{ 'is-invalid': state === false }"
      @open="load()"
      @input="$emit('input', $event === null ? null : $event.code)"
    >
      <template #no-options>
        <span v-if="loading">Loading options…</span>
        <span v-else-if="error">
          Options could not be loaded.
          <button
            type="button"
            class="btn btn-link btn-sm p-0"
            @click.prevent.stop="load(true)"
          >
            Retry
          </button>
        </span>
        <span v-else>No matching options.</span>
      </template>
    </v-select>
    <small
      v-if="error"
      class="text-danger d-block mt-25"
    >
      Options could not be loaded.
      <button
        type="button"
        class="btn btn-link btn-sm p-0 align-baseline"
        @click="load(true)"
      >
        Retry
      </button>
    </small>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import {
  formatClassifierOption,
  getClassifierSnapshot,
  preservedClassifierOption,
} from '@/libs/customsClassifiers'

export default {
  name: 'ClassifierSelect',
  components: { vSelect },
  props: {
    value: { type: [String, Number], default: null },
    classifierKey: { type: String, required: true },
    // Optional 2-letter country (e.g. 'FI') selecting a country-specific snapshot,
    // appended as a path segment: /v1/customs/classifiers/{key}/codes/{country}.
    // Null/empty means the generic snapshot (no country segment).
    country: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    // Whether this list serves an option whose code is an empty string, as the
    // Netherlands serves "No previous Document". It makes '' a value like any
    // other here — resolved to its description on mount and told apart from
    // null, which stays "nothing selected". Lists without such an option leave
    // it false, so a field they find empty still loads only when opened.
    emptyCodeSelectable: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    state: { type: Boolean, default: null },
  },
  data() {
    return {
      requestId: 0,
      snapshot: null,
      loading: false,
      error: null,
    }
  },
  computed: {
    // Bind an option object so an empty code remains a selection in vue-select.
    // Only a cleared selection emits null; the screen still stores the code.
    selectedOption() {
      if (this.value === null || this.value === undefined) return null
      return this.options.find(option => option.code === String(this.value)) || null
    },
    options() {
      // Tulli snapshots can repeat codes. Keep the first entry, which carries
      // any country overlay flags, and use the same list for screen rules.
      const seen = new Set()
      const options = (this.snapshot?.options || []).filter(option => {
        if (seen.has(option.code)) return false
        seen.add(option.code)
        return true
      })
      if (this.value !== null && this.value !== undefined && (this.value !== '' || this.emptyCodeSelectable)
        && !options.some(option => option.code === String(this.value))) {
        return [...options, preservedClassifierOption(this.value)]
      }
      return options
    },
  },
  watch: {
    classifierKey() {
      this.reloadList()
    },
    country() {
      this.reloadList()
    },
    value(newValue) {
      // A value filled in after mount — copying one declaration's transport
      // data onto another, say — still needs its description resolved.
      this.resolveValue(newValue)
    },
  },
  mounted() {
    this.resolveValue()
  },
  beforeDestroy() {
    this.requestId += 1
    document.removeEventListener('click', this.swallowStrayClick, true)
  },
  methods: {
    // vue-select selects on the option's own mousedown and stops it there, so
    // BootstrapVue never arms the guard it keeps for a press that starts inside
    // the dialog (its onDialogMousedown). The option is torn down before the
    // mouseup, so the browser resolves the click against whatever is under the
    // cursor by then — the backdrop, whenever the list overflowed past the
    // dialog — and the modal dismisses itself. The capture phase runs before
    // vue-select's handler, so arm a one-shot guard of our own here.
    onOptionMousedown(event) {
      if (!event.target.closest?.('.vs__dropdown-option')) {
        return
      }
      document.removeEventListener('click', this.swallowStrayClick, true)
      document.addEventListener('click', this.swallowStrayClick, true)
    },
    // The click that belongs to the option just selected, not to the backdrop.
    // A click that really did land on this field is an ordinary one.
    swallowStrayClick(event) {
      document.removeEventListener('click', this.swallowStrayClick, true)
      if (!this.$el.contains(event.target)) {
        event.stopPropagation()
      }
    },
    reloadList(force = false) {
      const hadLoad = !!this.snapshot || this.loading || !!this.error
      this.requestId += 1
      this.loading = false
      this.snapshot = null
      this.error = null
      // Keep unopened empty fields lazy, but refresh every list already used,
      // including one whose previous country failed to load.
      if (hadLoad || force) {
        this.load(force)
      } else {
        this.resolveValue()
      }
    },
    // A field that already holds a code would otherwise read as
    // '<code> — previously saved value' until someone opens the dropdown, so
    // load the list for it. Empty fields keep loading on open, unless the list
    // serves a selectable empty code and '' is therefore a value to resolve.
    resolveValue(value = this.value) {
      if (value === null || value === undefined) return
      if (value === '' && !this.emptyCodeSelectable) return
      if (this.snapshot || this.loading || this.error) return
      this.load()
    },
    load(force = false) {
      this.requestId += 1
      const { requestId } = this
      this.loading = true
      this.error = null
      return getClassifierSnapshot(this.classifierKey, { force, country: this.country })
        .then(snapshot => {
          if (requestId === this.requestId) this.snapshot = snapshot
        })
        .catch(error => {
          if (requestId === this.requestId) this.error = error
        })
        .finally(() => {
          if (requestId === this.requestId) this.loading = false
        })
    },
    // Shared with every other classifier dropdown, including the placeholder
    // kept for a saved value the list no longer carries.
    formatOption: formatClassifierOption,
    filterOption(option, label, search) {
      const query = (search || '').toLocaleLowerCase()
      return option.code.toLocaleLowerCase().includes(query)
        || option.label.toLocaleLowerCase().includes(query)
    },
  },
}
</script>
